/* eslint-disable no-console */
import 'isomorphic-fetch';
import { v4 as uuidV4 } from 'uuid';
import { _deserialize, _getItem, _saveItem } from '../utils';

const jwtDecode = require('jwt-decode');

/* istanbul ignore next */
function getRedirectUri() {
  if (!window.location.href.includes('login')) {
    return window.location.href;
  }
  let url = `${window.location.protocol}//${window.location.hostname}`;
  if (window.location.port) {
    url = `${url}:${window.location.port}`;
  }
  return url;
}
const navianceConfig = {
  clientId: process.env.NAVIANCE_CLIENT_ID,
  redirectUri: getRedirectUri(),
  authorizeUri: process.env.NAVIANCE_OAUTH_AUTHORIZE_URI,
  tokenUri: process.env.NAVIANCE_OAUTH_TOKEN_URI,
  cacheLocation: 'sessionStorage',
  claims:
  {
    id_token: {
      'given_name': {
        essential: true,
      },
      'family_name': {
        essential: true,
      },
      'email': {
        essential: true,
      },
      'https://oauth.naviance.com/oauth/claims/title': {
        essential: true,
      },
      'https://oauth.naviance.com/oauth/claims/school.id': {
        essential: true,
      },
      'https://oauth.naviance.com/oauth/claims/community.member': {
        essential: true,
      },
      'https://oauth.naviance.com/oauth/claims/community.admin': {
        essential: true,
      },
    },
  },
};

export const CONSTANTS = {
  ACCESS_TOKEN: 'access_token',
  EXPIRES_IN: 'expires_in',
  ID_TOKEN: 'id_token',
  CODE: 'code',
  STORAGE: {
    ACCESS_TOKEN_KEY: 'naviance.access.token.key',
    EXPIRATION_KEY: 'naviance.expiration.key',
    STATE_LOGIN: 'naviance.state.login',
    NONCE_IDTOKEN: 'naviance.nonce.idtoken',
    IDTOKEN: 'naviance.idtoken',
    CODE: 'naviance.code',
    REDIRECT_URI: 'naviance.redirecturi',
  },
};

/**
 * Handles authentication logic for Naviance OAuth2.
 */
class NavianceIdpService {
  constructor(config) {
    this._config = { ...config };
    this._oauthData = { hasCode: false };
  }

  /**
   * Serializes the parameters for the authorization endpoint URL and returns the serialized uri string.
   */
  _serializeRequest = (responseType, nonce, state) => {
    const str = [];
    /* istanbul ignore else */
    if (this._config !== null) {
      str.push(`?response_type=${responseType}`);
      str.push(`client_id=${encodeURIComponent(this._config.clientId)}`);
      str.push('scope=openid');
      str.push(`redirect_uri=${encodeURIComponent(this._config.redirectUri)}`);
      str.push(`state=${encodeURIComponent(state)}`);
      str.push(`claims=${encodeURIComponent(JSON.stringify(this._config.claims))}`);
      str.push(`nonce=${encodeURIComponent(nonce)}`);
    }
    return str.join('&');
  };

  /**
  * Creates a user object by decoding the id_token
  */
  _createUser(idToken) {
    let user = null;
    if (!idToken) {
      return null;
    }
    const parsedJson = jwtDecode(idToken);
    /* istanbul ignore else */
    if (parsedJson && Object.prototype.hasOwnProperty.call(parsedJson, 'aud')) {
      if (parsedJson.aud.toLowerCase() === this._config.clientId.toLowerCase()) {
        user = {
          jwtToken: idToken,
          idToken: parsedJson,
          userName: parsedJson.sub,
        };
      } else {
        console.log('IdToken has invalid aud field');
      }
    }
    return user;
  }

  _updateDataFromCache() {
    const code = _getItem(this._config.cacheLocation, CONSTANTS.STORAGE.CODE);
    this._oauthData.hasCode = !!code;
    const user = this.getCachedUser() || {};
    Object.assign(this._oauthData, user);
  }

  /**
   * Processes response from the authorization flow.
   */
  _getRequestInfo(query) {
    const parameters = _deserialize(query);
    const requestInfo = {
      valid: false,
      parameters: {},
      stateMatch: false,
    };
    if (Object.keys(parameters).length !== 0) {
      requestInfo.parameters = parameters;
      if (Object.prototype.hasOwnProperty.call(parameters, CONSTANTS.CODE)) {
        requestInfo.valid = true;

        // which call
        let stateResponse = '';
        if (Object.prototype.hasOwnProperty.call(parameters, 'state')) {
          stateResponse = parameters.state;
        } else {
          console.log('No state returned');
          return requestInfo;
        }
        if (stateResponse === _getItem(this._config.cacheLocation, CONSTANTS.STORAGE.STATE_LOGIN)) {
          requestInfo.stateMatch = true;
          return requestInfo;
        }
      }
    }

    return requestInfo;
  }

  /**
   * Checks if the URL fragment contains code.
   * @param {string} query  -  query passed from redirect page
   * @returns {Boolean} true if response contains code, false otherwise.
   */
  _isCallback = (query) => {
    const parameters = _deserialize(query);
    return (
      Object.prototype.hasOwnProperty.call(parameters, CONSTANTS.CODE)
    );
  };

  /**
   * Processes response from token request.
   */
  _handleTokenResponse(response) {
    const responseKeys = Object.keys(response);
    const expectedKeys = [CONSTANTS.ACCESS_TOKEN, CONSTANTS.ID_TOKEN, CONSTANTS.EXPIRES_IN];
    if (expectedKeys.every((value) => responseKeys.includes(value))) {
      _saveItem(this._config.cacheLocation, CONSTANTS.STORAGE.ACCESS_TOKEN_KEY, response[CONSTANTS.ACCESS_TOKEN]);
      _saveItem(this._config.cacheLocation, CONSTANTS.STORAGE.EXPIRATION_KEY, Date.now() + (Number(response[CONSTANTS.EXPIRES_IN]) * 1000));
      _saveItem(this._config.cacheLocation, CONSTANTS.STORAGE.IDTOKEN, response[CONSTANTS.ID_TOKEN]);
      this._updateDataFromCache();
    } else {
      throw new Error('Invalid response');
    }
  }

  /* eslint-disable class-methods-use-this */
  /* istanbul ignore next */
  _navigate(url) {
    window.location.replace(url);
  }

  /**
   * Creates a new user object by decoding id_token from the cache.
   * @returns {User} user object
   */
  getCachedUser() {
    const idtoken = _getItem(this._config.cacheLocation, CONSTANTS.STORAGE.IDTOKEN);
    return this._createUser(idtoken);
  }

  /**
   * Returns cached JWT token.
   */
  getCachedToken() {
    return _getItem(this._config.cacheLocation, CONSTANTS.STORAGE.IDTOKEN);
  }

  // starts login flow.
  login() {
    if (this._loginInProgress) {
      console.log('Login in progress');
      return;
    }
    const expectedState = uuidV4();
    const idTokenNonce = uuidV4();
    _saveItem(this._config.cacheLocation, CONSTANTS.STORAGE.STATE_LOGIN, expectedState);
    _saveItem(this._config.cacheLocation, CONSTANTS.STORAGE.REDIRECT_URI, this._config.redirectUri);
    _saveItem(this._config.cacheLocation, CONSTANTS.STORAGE.NONCE_IDTOKEN, idTokenNonce);
    const urlNavigate = `${this._config.authorizeUri}${this._serializeRequest('code', idTokenNonce, expectedState)}`;
    this._loginInProgress = true;
    this._navigate(urlNavigate);
  }

  clear() {
    _saveItem(this._config.cacheLocation, CONSTANTS.STORAGE.ACCESS_TOKEN_KEY, '');
    _saveItem(this._config.cacheLocation, CONSTANTS.STORAGE.EXPIRATION_KEY, 0);
    _saveItem(this._config.cacheLocation, CONSTANTS.STORAGE.CODE, '');
    _saveItem(this._config.cacheLocation, CONSTANTS.STORAGE.IDTOKEN, '');
    _saveItem(this._config.cacheLocation, CONSTANTS.STORAGE.STATE_LOGIN, '');
    _saveItem(this._config.cacheLocation, CONSTANTS.STORAGE.NONCE_IDTOKEN, '');
    _saveItem(this._config.cacheLocation, CONSTANTS.STORAGE.REDIRECT_URI, '');
    this._oauthData = { hasCode: false };
  }

  // starts logout flow.
  logout() {
    this.clear();
    // TODO: call naviance logout??
  }

  /**
   * Process redirect from Naviance OAuth2 Authorization flow.
   * Returns _oauthData which would have hasCode flag set to true if code is received.
   */
  processIdpCallback() {
    const query = window.location.search;
    if (this._isCallback(query)) {
      // callback can come from login
      const requestInfo = this._getRequestInfo(query);
      window.history.replaceState({}, document.title, window.location.pathname);
      if (requestInfo.stateMatch) {
        _saveItem(this._config.cacheLocation, CONSTANTS.STORAGE.CODE, requestInfo.parameters[CONSTANTS.CODE]);
        this._loginInProgress = false;
        this._updateDataFromCache();
        return { ...this._oauthData };
      } else {
        // state did not match, set error
        return {
          stateMismatch: 'Invalid state response',
        };
      }
    }
    return undefined;
  }

  /**
   * Performs the second leg of OAuth2 to get the token.
   */
  acquireToken() {
    const checkStatus = (response) => {
      if (response.status >= 200 && response.status < 300) {
        return response;
      } else {
        const error = new Error(response.statusText);
        error.response = response;
        throw error;
      }
    };

    const str = [];
    str.push('?grant_type=authorization_code');
    str.push(`redirect_uri=${encodeURIComponent(_getItem(this._config.cacheLocation, CONSTANTS.STORAGE.REDIRECT_URI))}`);
    str.push(`code=${encodeURIComponent(_getItem(this._config.cacheLocation, CONSTANTS.STORAGE.CODE))}`);
    const endpoint = `${this._config.tokenUri}${str.join('&')}`;

    return fetch(endpoint, { method: 'POST' })
      .then(checkStatus)
      .then((response) => response.json())
      .then((response) => {
        this._handleTokenResponse(response);
        return { ...this._oauthData };
      });
  }
}
const idpService = new NavianceIdpService(navianceConfig);
export { idpService as default };
